import axios from 'axios'

const API = process.env.REACT_APP_API_URL

let mapaAudiosCache = null
let cargandoPromesa = null

// "Modo lento" para estudiantes principiantes: cuando esta activo, toda
// palabra o frase que se reproduce (grabacion real o voz del navegador)
// suena mas despacio para que se pueda retener mejor. Se guarda aqui como
// variable del modulo (no en React state) para que hablar() la pueda leer
// sin que cada pantalla tenga que pasarsela como parametro.
let modoLento = false
export function establecerModoLento(valor) { modoLento = valor }
export function obtenerModoLento() { return modoLento }

function normalizarTexto(texto) {
  return (texto || '').trim().toLowerCase().replace(/\s+/g, ' ')
}

// Carga el mapa de audios grabados UNA vez por sesion y lo guarda en memoria
export function precargarMapaAudios() {
  if (mapaAudiosCache || cargandoPromesa) return cargandoPromesa
  cargandoPromesa = axios.get(API + '/audios/mapa')
    .then(r => { mapaAudiosCache = r.data.datos || {} })
    .catch(() => { mapaAudiosCache = {} })
  return cargandoPromesa
}

// Voz robotica del navegador (respaldo, se usa si no hay audio real grabado)
// onFin (opcional): se llama cuando la lectura termina (o falla), para que
// quien pidio hablar() sepa el momento exacto en que puede continuar (por
// ejemplo, la escena animada que avanza de una linea de dialogo a la
// siguiente solo cuando el navegador de verdad termino de decir la linea
// actual, en vez de adivinar un tiempo fijo).
//
// genero (opcional): 'm' o 'f'. Usa una voz masculina o femenina si el
// dispositivo tiene una; si no, cambia el tono de la voz disponible.
const VOZ_MASCULINA = /(pablo|raul|jorge|juan|diego|carlos|alvaro|gonzalo|jose|miguel|enrique|ricardo|andres|alejandro|\bmale\b)/i
const VOZ_FEMENINA  = /(helena|sabina|laura|paulina|monica|m\u00f3nica|elvira|dalia|salome|paola|lucia|marisol|maria|sofia|ximena|esperanza|\bfemale\b)/i

function hablarConVozNavegador(texto, onFin, genero) {
  if (!window.speechSynthesis) { if (onFin) onFin(); return }
  window.speechSynthesis.cancel()

  // Chrome tiene un bug conocido: si un texto tarda mas de ~15 segundos
  // en leerse, el motor de voz se "pausa" solo y corta la lectura a la
  // mitad. El truco para evitarlo es llamar resume() cada tanto mientras
  // dura la lectura. ANTES este intervalo se apagaba con un setTimeout
  // fijo (5s), asi que cualquier texto largo se cortaba apenas pasaban
  // esos 5 segundos. Ahora el intervalo se apaga solo cuando la lectura
  // realmente termina (o falla), con un limite de seguridad de 2 minutos
  // por si el evento no llega.
  const intervalId = setInterval(() => window.speechSynthesis.resume(), 200)
  const idSeguridad = setTimeout(() => clearInterval(intervalId), 120000)
  let yaTermino = false
  function detenerIntervalo() {
    clearInterval(intervalId)
    clearTimeout(idSeguridad)
    if (!yaTermino) { yaTermino = true; if (onFin) onFin() }
  }

  const u = new SpeechSynthesisUtterance(texto)
  u.lang = 'es-MX'; u.rate = modoLento ? 0.6 : 0.85; u.pitch = 1.0
  u.onend = detenerIntervalo
  u.onerror = detenerIntervalo
  let encontroGenero = false
  const elegirVoz = (voces) => {
    // Con genero pedido: primero una voz de ese genero (es-CO, luego es-MX, luego cualquier es)
    if (genero === 'm' || genero === 'f') {
      const patron = genero === 'm' ? VOZ_MASCULINA : VOZ_FEMENINA
      const delGenero = voces.find(v => v.lang === 'es-CO' && patron.test(v.name))
        || voces.find(v => v.lang === 'es-MX' && patron.test(v.name))
        || voces.find(v => v.lang.startsWith('es') && patron.test(v.name))
      if (delGenero) { encontroGenero = true; return delGenero }
    }
    return voces.find(v => v.lang === 'es-CO')
      || voces.find(v => v.lang === 'es-MX')
      || voces.find(v => v.name === 'Juan')
      || voces.find(v => v.name === 'Paulina')
      || voces.find(v => v.lang.startsWith('es'))
  }
  function configurarYHablar() {
    window.speechSynthesis.onvoiceschanged = null // evita que hable dos veces
    const voz = elegirVoz(window.speechSynthesis.getVoices())
    if (voz) u.voice = voz
    // Sin voz del genero pedido en este dispositivo: se simula con el tono
    if (!encontroGenero) {
      if (genero === 'm') u.pitch = 0.7
      else if (genero === 'f') u.pitch = 1.2
    }
    window.speechSynthesis.speak(u)
  }
  if (window.speechSynthesis.getVoices().length > 0) {
    configurarYHablar()
  } else {
    window.speechSynthesis.onvoiceschanged = configurarYHablar
  }
}

// Funcion principal: usa la grabacion real de Ivan si existe, si no cae a la voz del navegador.
// onFin (opcional): se llama cuando termina de sonar el audio (grabado o de
// voz), sin importar cual de los dos caminos se uso.
//
// opciones.genero (opcional): 'm' o 'f'. Las grabaciones reales son de voz
// masculina, asi que para un personaje femenino se omite la grabacion y se
// usa una voz femenina del dispositivo.
export function hablar(texto, onFin, opciones) {
  const genero = opciones && opciones.genero
  if (genero === 'f') {
    hablarConVozNavegador(texto, onFin, genero)
    return
  }
  if (!mapaAudiosCache) {
    // Aun no cargo el mapa (primera vez muy rapido) -> usa voz de navegador esta vez
    precargarMapaAudios()
    hablarConVozNavegador(texto, onFin, genero)
    return
  }
  const key = normalizarTexto(texto)
  const audioUrl = mapaAudiosCache[key]
  if (audioUrl) {
    const audio = new Audio(audioUrl)
    // playbackRate mas bajo = se escucha mas despacio sin sonar como un
    // disco rayado (los navegadores modernos mantienen el tono correcto
    // automaticamente gracias a preservesPitch, activado por defecto).
    audio.playbackRate = modoLento ? 0.72 : 1
    let yaTermino = false
    const terminar = () => { if (!yaTermino) { yaTermino = true; if (onFin) onFin() } }
    audio.onended = terminar
    audio.onerror = () => hablarConVozNavegador(texto, onFin, genero)
    audio.play().catch(() => hablarConVozNavegador(texto, onFin, genero))
  } else {
    hablarConVozNavegador(texto, onFin, genero)
  }
}
